// Phase JB.3b — Referral capture on land.
//
// Boot-time module. Called once from src/main.tsx before render.
// Parses `?ref=CODE` from the URL. If valid (6-char alphanumeric),
// persists to localStorage under STORAGE_KEY. After sign-up, the
// AuthProvider (or any subsequent auth-aware code) can call
// flushPendingReferral() to write it via the record_pending_referral
// RPC, which the DB trigger then attributes on profiles INSERT.
//
// Vendor-storage exception: localStorage is permitted for this anon
// capture path because it must survive the auth round-trip. The Bible
// rule against localStorage governs app-level state — this is a
// one-shot session token equivalent to Supabase's own auth storage.

import { getBrowserClient } from "../supabase/browser.ts";

export const STORAGE_KEY = "strikza_pending_ref";
const CODE_RE = /^[A-Z0-9]{6}$/;

export function captureReferralOnLand(search: string = typeof window !== "undefined" ? window.location.search : ""): string | null {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get("ref");
    if (!raw) return null;
    const code = raw.toUpperCase().trim();
    if (!CODE_RE.test(code)) return null;
    if (typeof localStorage !== "undefined") {
      const existing = localStorage.getItem(STORAGE_KEY);
      // First-touch attribution: never overwrite an earlier code.
      if (!existing) localStorage.setItem(STORAGE_KEY, code);
    }
    return code;
  } catch {
    return null;
  }
}

export function readPendingReferral(): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPendingReferral(): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Called by the AuthProvider after a successful sign-up. Idempotent:
 * the RPC handles duplicates, and we clear the storage either way.
 */
export async function flushPendingReferral(): Promise<boolean> {
  const code = readPendingReferral();
  if (!code) return false;
  try {
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (sb as any).rpc("record_pending_referral", { p_code: code });
    if (error) {
      console.warn("[captureReferralOnLand] rpc failed:", error.message);
      return false;
    }
    clearPendingReferral();
    return true;
  } catch (e) {
    console.warn("[captureReferralOnLand] flush failed:", (e as Error).message);
    return false;
  }
}
